import {useNavigate, useSearchParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useAuth} from "../../../../hooks/useAuth";
import {StatsCards} from "./StatsCards.tsx";
import {QuickActions} from "./QuickActions.tsx";
import {LicenseSummary} from "./LicenseSummary.tsx";
import {WelcomeCard} from "./WelcomeCard.tsx";
import {PaymentSuccessDialog} from "./PaymentSuccessDialog.tsx";
import {AdditionalEntitlementCards} from "./AdditionalEntitlementCards.tsx";
import {
  useGetPortalConfigQuery,
  useGetPortalLicensesQuery,
  useGetPortalTeamQuery
} from "../../../../store/generated/api.ts";

export function DashboardView() {
  const {t} = useTranslationWithKeys();
  const {user} = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'cancel' | null>(null);

  // Fetch real license data from API
  const {data: licensesData, isLoading: licensesLoading} = useGetPortalLicensesQuery({});

  // Fetch team data to get actual member count
  const {data: teamData} = useGetPortalTeamQuery({page: 0, size: 10});

  // Fetch feature configuration
  const {data: configData} = useGetPortalConfigQuery();

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success' || payment === 'cancel') {
      setPaymentStatus(payment);
      setSearchParams(prev => {
        prev.delete('payment');
        return prev;
      });
    }
  }, [searchParams, setSearchParams]);

  // Calculate stats from real license data
  const stats = {
    totalLicenses: licensesData?.pagination?.total || 0,
    activeLicenses: licensesData?.licenses?.filter(license => license.status === 'ACTIVE').length || 0,
    expiringLicenses: licensesData?.expiringSoon || 0,
    teamMembers: teamData?.team?.members || 0,
    availableSeats: licensesData?.totalAvailableSeats || 0,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1>{t('dashboard.title')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('dashboard.welcomeMessage')}
        </p>
      </div>

      <StatsCards stats={stats} config={configData}/>

      <AdditionalEntitlementCards config={configData}/>

        <QuickActions
            onDownloadClick={() => navigate("/download")}
            onInviteClick={() => navigate("/team")}
            availableSeats={stats.availableSeats}
        />

        <LicenseSummary
            licenses={licensesData?.licenses || []}
            onViewAllClick={() => navigate("/licenses")}
        />

        <WelcomeCard
            onSupportClick={() => navigate("/support")}
        />

      <PaymentSuccessDialog
          open={paymentStatus === 'success'}
          onOpenChange={(open) => !open && setPaymentStatus(null)}
      />
    </div>
  );
}